import type {RouteRecord} from "vite-react-ssg";

/**
 * vite-react-ssg mutates every route's loader before customCreateRouter runs. Capture the declared
 * loaders first, including lazy modules resolved during its initial preload. Restoring them removes
 * static-manifest requests from loader-less routes without changing real loaders, actions or params.
 */
export function preserveRouteLoaders(source: RouteRecord[]) {
	const entries = source.map(route => {
		let loader = route.loader;
		const children = route.children && preserveRouteLoaders(route.children);
		const lazy = route.lazy;
		const prepared = {...route, children: children?.routes,
			...(typeof lazy === "function" ? {lazy: async () => {
				const module = await lazy();
				// React Router gives an explicitly declared loader precedence over a lazy export.
				loader ??= module.loader;
				return module;
			}} : {}),
		} as RouteRecord;
		return {prepared, restore: (generated: RouteRecord): RouteRecord => ({
			...generated,
			loader,
			...(children ? {children: children.restore(generated.children ?? [])} : {}),
		} as RouteRecord)};
	});
	return {
		routes: entries.map(entry => entry.prepared),
		restore(generated: RouteRecord[]): RouteRecord[] {
			if (generated.length !== entries.length) throw new Error("SSG route tree changed during conversion");
			return generated.map((route, index) => entries[index].restore(route));
		},
	};
}

interface LoaderHydrationData {
	readonly loaderData?: Record<string, unknown>;
}

/**
 * Completes the inline record for matched loader-less routes. The caller excludes real loaders:
 * missing real data must still run its loader, not be replaced by an invented null. Existing server
 * values and errors remain intact for React Router's own hydration and error deserialization.
 */
export function completeStaticLoaderHydrationData<T extends LoaderHydrationData>(
	hydrationData: T | undefined,
	matchedRouteIds: readonly (string | undefined)[],
): (T & {loaderData: Record<string, unknown>}) | undefined {
	if (hydrationData === undefined) {
		return undefined;
	}
	const loaderData = {...hydrationData.loaderData};
	for (const routeId of matchedRouteIds) {
		if (routeId !== undefined && !Object.prototype.hasOwnProperty.call(loaderData, routeId)) {
			loaderData[routeId] = null;
		}
	}
	return {...hydrationData, loaderData};
}
